'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';
import { useSnackbar } from '@/hooks/useSnackbar';

interface SnackbarContextType {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
  showUndo: (message: string, onUndo: () => void) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const { snackbarOpen, snackbarMessage, snackbarSeverity, showSuccess, showError, showInfo, showWarning, showUndo, snackbarUndo, hideSnackbar } = useSnackbar();

  const contextValue: SnackbarContextType = {
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showUndo,
  };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar open={snackbarOpen} autoHideDuration={snackbarUndo ? 6000 : 4000} onClose={hideSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          onClose={hideSnackbar}
          action={
            snackbarUndo ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  hideSnackbar();
                  snackbarUndo();
                }}
                sx={{ fontWeight: 700, textTransform: 'none' }}
              >
                Undo
              </Button>
            ) : undefined
          }
          severity={snackbarSeverity}
          variant="standard"
          sx={{
            minWidth: '300px',
            '& .MuiAlert-message': {
              fontWeight: 500,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbarContext() {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbarContext must be used within a SnackbarProvider');
  }
  return context;
}
